import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    Divider,
} from '@mui/material';
import { Close, CalendarMonth } from '@mui/icons-material';

const DepartureDateDetailDialog = ({ open, onClose, departureDate }) => {
    if (!departureDate) return null;

    // Calculate duration
    const calculateDuration = () => {
        const departure = new Date(departureDate.departure_date);
        const returnDate = new Date(departureDate.return_date);
        const diffTime = Math.abs(returnDate - departure);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const duration = calculateDuration();

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant='h5'>Departure Date Details</Typography>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Package Information */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant='h6' gutterBottom>
                                    Package Information
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant='subtitle2'
                                            color='textSecondary'
                                        >
                                            Package Name
                                        </Typography>
                                        <Typography
                                            variant='body1'
                                            fontWeight='bold'
                                        >
                                            {departureDate.package_name ||
                                                'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant='subtitle2'
                                            color='textSecondary'
                                        >
                                            Category
                                        </Typography>
                                        <Chip
                                            label={
                                                departureDate
                                                    .category_name ||
                                                'Uncategorized'
                                            }
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant='subtitle2'
                                            color='textSecondary'
                                        >
                                            Description
                                        </Typography>
                                        <Typography variant='body1'>
                                            {departureDate.description ||
                                                'No description available'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Date Information */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant='h6' gutterBottom>
                                    Date Information
                                </Typography>
                                <Box
                                    display='flex'
                                    flexDirection='column'
                                    gap={2}
                                >
                                    <Box>
                                        <Typography
                                            variant='subtitle2'
                                            color='textSecondary'
                                        >
                                            Departure Date
                                        </Typography>
                                        <Typography
                                            variant='h6'
                                            color='primary'
                                        >
                                            {new Date(
                                                departureDate.departure_date
                                            ).toLocaleDateString('en-US', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant='subtitle2'
                                            color='textSecondary'
                                        >
                                            Return Date
                                        </Typography>
                                        <Typography
                                            variant='h6'
                                            color='primary'
                                        >
                                            {new Date(
                                                departureDate.return_date
                                            ).toLocaleDateString('en-US', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                            })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Trip Summary */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant='h6' gutterBottom>
                                    Trip Summary
                                </Typography>
                                <Box
                                    display='flex'
                                    flexDirection='column'
                                    gap={2}
                                >
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                        alignItems='center'
                                    >
                                        <Typography variant='subtitle2'>
                                            Duration:
                                        </Typography>
                                        <Chip
                                            label={`${duration} days`}
                                            color='primary'
                                            variant='filled'
                                        />
                                    </Box>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                        alignItems='center'
                                    >
                                        <Typography variant='subtitle2'>
                                            Status:
                                        </Typography>
                                        <Chip
                                            label={
                                                departureDate.is_active
                                                    ? 'Active'
                                                    : 'Inactive'
                                            }
                                            color={
                                                departureDate.is_active
                                                    ? 'success'
                                                    : 'default'
                                            }
                                        />
                                    </Box>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                        alignItems='center'
                                    >
                                        <Typography variant='subtitle2'>
                                            Created:
                                        </Typography>
                                        <Typography variant='body2'>
                                            {new Date(
                                                departureDate.created_at
                                            ).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    <Box
                                        display='flex'
                                        justifyContent='space-between'
                                        alignItems='center'
                                    >
                                        <Typography variant='subtitle2'>
                                            Last Updated:
                                        </Typography>
                                        <Typography variant='body2'>
                                            {new Date(
                                                departureDate.updated_at
                                            ).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Package Details */}
                    {departureDate.package && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant='h6' gutterBottom>
                                        Package Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography
                                                variant='subtitle2'
                                                color='textSecondary'
                                            >
                                                Transport
                                            </Typography>
                                            <Typography variant='body1'>
                                                {departureDate.package
                                                    .transport || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography
                                                variant='subtitle2'
                                                color='textSecondary'
                                            >
                                                Flight Class
                                            </Typography>
                                            <Typography variant='body1'>
                                                {departureDate.package.flight ||
                                                    'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography
                                                variant='subtitle2'
                                                color='textSecondary'
                                            >
                                                Max Participants
                                            </Typography>
                                            <Typography variant='body1'>
                                                {departureDate.package
                                                    .max_participants || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant='contained'>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DepartureDateDetailDialog;
